import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import type { BiltEvent, EventStatus } from '../../types/event';
import Footer from '../Footer';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { useOnboarding } from '../../hooks/useOnboarding';
import { SetupChecklistCard } from '../onboarding/OnboardingCards';
import {
  buildOrganizerChecklist,
  shouldShowOrganizerChecklist,
  type ChecklistStep,
} from '../../utils/onboarding';

const CalendarIcon: React.FC = () => (
  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const STATUS_STYLES: Record<EventStatus, string> = {
  draft: 'bg-[#141414] text-gray-300 border border-white/[0.08]',
  published: 'bg-emerald-950/40 text-emerald-300 border border-emerald-800/60',
  completed: 'bg-orange-950/30 text-orange-200 border border-orange-800/50',
  cancelled: 'bg-red-950/40 text-red-300 border border-red-800/50',
};

const STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function formatEventDate(startAt: string | null): string {
  if (!startAt) return 'Date TBD';
  const date = new Date(startAt.replace(' ', 'T'));
  if (isNaN(date.getTime())) return 'Date TBD';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Bilt organizer home (`/organizer`).
 *
 * Lists the organizer's own events with a create CTA. Event detail/admin,
 * classes, registrations, and CSV export live on event admin screens.
 */
const OrganizerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const onboarding = useOnboarding();

  const [events, setEvents] = useState<BiltEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getOrganizerEvents();
      setEvents(data);
    } catch (err: any) {
      console.error('Failed to load organizer events:', err);
      setError(err.message || 'Failed to load your events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadEvents();
    }
  }, [authLoading, isAuthenticated, loadEvents]);

  const checklistSteps = useMemo(
    () => buildOrganizerChecklist(events, onboarding.state),
    [events, onboarding.state]
  );
  const showChecklist = shouldShowOrganizerChecklist(onboarding.state, checklistSteps);

  const handleChecklistAction = (step: ChecklistStep) => {
    if (step.id === 'classes') void onboarding.markStep('organizer_classes');
    if (step.id === 'registrations') void onboarding.markStep('organizer_registrations');
    if (step.id === 'judges') void onboarding.markStep('organizer_judges');
    if (step.href) navigate(step.href);
  };

  if (authLoading || loading) {
    return (
      <>
        <PageLoader message="Loading your events…" />
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          eyebrow="Organizer"
          title="Your events"
          description={
            events.length === 0
              ? 'Registration, judging, and awards without spreadsheets.'
              : `${events.length} ${events.length === 1 ? 'event' : 'events'}`
          }
          backLink={{ label: 'Back to Garage', to: '/garage' }}
          actions={
            events.length > 0 ? (
              <button
                type="button"
                onClick={() => navigate('/organizer/events/new')}
                className="btn-primary min-h-[44px] shrink-0 cursor-pointer"
              >
                Create event
              </button>
            ) : null
          }
        />

        {showChecklist && (
          <SetupChecklistCard
            title="Organizer checklist"
            steps={checklistSteps}
            onDismiss={() => void onboarding.dismissOrganizerChecklist()}
            onStepAction={handleChecklistAction}
          />
        )}

        {error ? (
          <EmptyState
            icon={<CalendarIcon />}
            title="Couldn’t load your events"
            description={error}
            action={
              <Button variant="primary" onClick={loadEvents}>
                Try again
              </Button>
            }
          />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<CalendarIcon />}
            title="No events yet"
            description="Create an event to open registration, assign judges, and run awards without spreadsheets."
            action={
              <button
                type="button"
                onClick={() => navigate('/organizer/events/new')}
                className="btn-primary min-h-[44px]"
              >
                Create event
              </button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {events.map((event) => (
              <Link
                key={event.id}
                to={`/organizer/events/${event.id}`}
                className="card-interactive block overflow-hidden flex flex-col"
              >
                {event.flyer_path ? (
                  <img
                    src={apiService.getFileUrl(event.flyer_path) || event.flyer_path}
                    alt=""
                    className="w-full aspect-[16/10] object-cover border-b border-white/[0.06]"
                  />
                ) : null}
                <div className="p-4 sm:p-5 flex flex-col gap-3 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <span
                      className={`text-xs px-2.5 py-1 rounded-md whitespace-nowrap font-medium ${STATUS_STYLES[event.status]}`}
                    >
                      {STATUS_LABELS[event.status]}
                    </span>
                  </div>
                  <h2 className="heading-card leading-tight">{event.title}</h2>
                  <div className="text-sm text-muted space-y-1 mt-auto">
                    <p>{formatEventDate(event.start_at)}</p>
                    {(event.city || event.state || event.location) && (
                      <p className="truncate">
                        {[event.city, event.state].filter(Boolean).join(', ') || event.location}
                      </p>
                    )}
                    <p className="text-meta font-mono">/events/{event.slug}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OrganizerDashboard;
